import React, { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons } from '@/design_system/Footer';
import InputText from '@/design_system/InputText';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import { ShipmentWithRelations, useUpdateShipmentTrackingId } from '@/models/shipment';

export const TrackingIdModal = ({
  shipment,
  children,
}: {
  shipment: ShipmentWithRelations;
  children: React.ReactNode;
}) => {
  const { t } = useLingui();

  const [isOpen, setIsOpen] = useState(false);
  const [trackingId, setTrackingId] = useState(shipment.trackingId ?? '');

  const { mutateAsync: updateShipmentTrackingId, isPending: isUpdateShipmentTrackingIdPending } =
    useUpdateShipmentTrackingId(shipment.id);

  return (
    <Dialog
      trigger={children}
      title={<Trans id="shipment.trackingNumber.modal.title">Add a tracking number</Trans>}
      style={{ maxWidth: '500px' }}
      isOpen={isOpen}
      onOpenChange={(isOpen) => {
        if (isOpen || !isUpdateShipmentTrackingIdPending) {
          setIsOpen(isOpen);
        }
      }}
    >
      <main>
        <Stack gap="8px">
          <Paragraph>
            <Trans id="shipment.trackingNumber.modal.text">
              You can add a tracking number to this shipment in order to get tracking updates for
              you and the recipient.
            </Trans>
          </Paragraph>
          <InputText
            aria-label={t({
              id: 'shipment.trackingNumber.modal.label',
              message: 'Tracking number',
            })}
            placeholder={t({
              id: 'shipment.trackingNumber.modal.placeholder',
              message: 'Add courier tracking number',
            })}
            value={trackingId}
            onChange={setTrackingId}
          />
        </Stack>
      </main>
      <Footer>
        <FooterButtons>
          <Button
            variant="secondary"
            size="medium"
            onPress={() => setIsOpen(false)}
            disabled={isUpdateShipmentTrackingIdPending}
          >
            <Trans id="shipment.trackingNumber.modal.cancel">Cancel</Trans>
          </Button>
          <Button
            variant="primary"
            size="medium"
            onPress={() => {
              updateShipmentTrackingId(trackingId !== '' ? trackingId : null).then(() => {
                setIsOpen(false);
              });
            }}
            isLoading={isUpdateShipmentTrackingIdPending}
            disabled={isUpdateShipmentTrackingIdPending}
          >
            <Trans id="shipment.trackingNumber.modal.save">Add tracking number</Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </Dialog>
  );
};
